import type { TransferData } from './payment'

// Common base for all locked quote data
interface LockedQuoteBase {
  lockedAt: string
  originalQuote?: FormattedQuote
}

// Formatted quote interface (used across components)
export interface FormattedQuote {
  sourceAmount: number
  destinationAmount: number
  exchangeRate: number
  cadUsdcRate?: number
  fees: {
    amount: number
    currency: string
    percentage: number
  }
  expiresAt: Date
  paymentMethod: string
  quoteType: 'fromExact' | 'toExact'
}

// Swap quote payload for API calls
export interface SwapQuotePayload {
  leftSideValue: number
  destinationCountry: string
  destinationCurrency: string
  paymentMethod: string
  beneficiaryRefId?: string
}

// Pending quote data (before authentication)
export interface PendingQuoteData extends LockedQuoteBase {
  originalQuote: FormattedQuote
  transferData: TransferData
  pendingSwapQuote: true
}

// Prepared quote data (authenticated, ready for swap quote creation)
export interface PreparedQuoteData extends LockedQuoteBase {
  originalQuote: FormattedQuote
  swapQuotePayload: SwapQuotePayload
  needsSwapQuoteCall: true
}

// Real swap quote data (after swap quote API call)
export interface RealSwapQuoteData extends LockedQuoteBase {
  // SwapQuoteResponse properties
  status: 'success'
  message: string
  id: string // Intent UUID
  requirements: object
  missing: {
    travelRule: string[]
    beneficiaryAccount: string[]
  }
  response: object
  paytrie: {
    id: number
    quoteId: number
    timestamp: string
    cadusd: number
    usdusdc: number
  }
  cpn: {
    quotes: Array<{
      id: number
      quoteUuid: string
      blockchain: string
      createDate: string
      destinationAmount: string
      destinationCountry: string
      destinationCurrency: string
      exchangeRate: string
      exchangeRatePair: string
      feeAmount: string
      feeCurrency: string
      paymentMethodType: string
      quoteExpireDate: string
      senderCountry: string
      sourceAmount: string
      sourceCurrency: string
    }>
    cachedQuotes: boolean
    quoteType: string
  }
  // Additional properties
  originalQuote: FormattedQuote
  isRealSwapQuote: true
  swapQuotePayload?: SwapQuotePayload
}

// Union type for all possible locked quote data structures
export type LockedQuoteData =
  | PendingQuoteData
  | PreparedQuoteData
  | RealSwapQuoteData

// Type guard functions for discriminating union types
export function isPendingQuoteData(
  data: LockedQuoteData
): data is PendingQuoteData {
  return 'pendingSwapQuote' in data && data.pendingSwapQuote === true
}

export function isPreparedQuoteData(
  data: LockedQuoteData
): data is PreparedQuoteData {
  return 'needsSwapQuoteCall' in data && data.needsSwapQuoteCall === true
}

export function isRealSwapQuoteData(
  data: LockedQuoteData
): data is RealSwapQuoteData {
  return 'isRealSwapQuote' in data && data.isRealSwapQuote === true
}
